import React, { PropsWithChildren } from 'react';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { Check, Close, ContentCopy } from '@mui/icons-material';
import { Patient } from '@models';
import { AlertService } from '../../../core/services/alert.service';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';

export type PatientInfoContactProps = PropsWithChildren<{
  alertService: AlertService;
  patient: Patient;
  capsuleEligibilityCheckInProgress: boolean;
  capsuleDeliveryAvailable: boolean;
}>;

export function PatientInfoContact({
  alertService,
  patient,
  capsuleEligibilityCheckInProgress,
  capsuleDeliveryAvailable,
}: PatientInfoContactProps): React.JSX.Element {
  function displayPhoneNumber(phoneNumber: string): string {
    if (phoneNumber) {
      let phone = phoneNumber.replace(/\D/g, '');
      const match = phone.match(/^(\d{1,3})(\d{0,3})(\d{0,4})$/);
      if (match) {
        phone = `(${match[1]}${match[2] ? ') ' : ''}${match[2]}${match[3] ? '-' : ''}${match[3]}`;
      }
      return phone;
    }

    return phoneNumber;
  }

  function handleCopyEmail(email: string): () => void {
    return () => {
      copyEmail(email);
    };
  }

  function handleCopyPhoneNumber(phoneNumber: string): () => void {
    return () => {
      copyPhoneNumber(phoneNumber);
    };
  }

  async function copyEmail(email: string): Promise<void> {
    await navigator.clipboard.writeText(email);
    alertService.success('Email copied to the clipboard');
  }

  async function copyPhoneNumber(phoneNumber: string): Promise<void> {
    await navigator.clipboard.writeText(phoneNumber);
    alertService.success('Phone number copied to the clipboard');
  }

  return (
    <>
      <Box sx={{ display: 'flex', marginTop: '4px' }}>
        <Box>
          <PhoneOutlinedIcon sx={{ fontSize: '18px', marginRight: '5px' }} />
        </Box>
        <Typography
          component={'div'}
          variant={'body2'}
          onClick={handleCopyPhoneNumber(patient.user?.phone ? patient.user.phone : patient.phone)}
          sx={{ marginTop: '1px' }}
        >
          {displayPhoneNumber(patient.user?.phone ? patient.user.phone : patient.phone)}
        </Typography>
        <Box>
          <Tooltip title='Copy phone number'>
            <IconButton
              onClick={handleCopyPhoneNumber(patient.user?.phone ? patient.user.phone : patient.phone)}
              sx={{ padding: 0, marginLeft: '8px' }}
            >
              <ContentCopy sx={{ fontSize: '14px', color: 'white' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', marginTop: '4px' }}>
        <Box>
          <EmailOutlinedIcon sx={{ fontSize: '18px', marginRight: '5px' }} />
        </Box>
        <Typography
          component={'div'}
          variant={'body2'}
          onClick={handleCopyEmail(patient.user?.email ? patient.user.email : patient.email)}
          sx={{ marginTop: '1px' }}
        >
          {patient.user?.email ? patient.user.email : patient.email}
        </Typography>
        <Box>
          <Tooltip title='Copy email'>
            <IconButton
              onClick={handleCopyEmail(patient.user?.email ? patient.user.email : patient.email)}
              sx={{ padding: 0, marginLeft: '8px' }}
            >
              <ContentCopy sx={{ fontSize: '14px', color: 'white' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {patient.address && (
        <Box style={{ display: 'flex', flexDirection: 'row', marginTop: '12px' }}>
          <Box>
            <PlaceOutlinedIcon sx={{ fontSize: '18px', marginRight: '5px' }} />
          </Box>
          <Box>
            <Typography variant={'body2'}>{patient.address.address1}</Typography>
            {patient.address.address2 && (
              <Box>
                <Typography variant={'body2'}>{patient.address.address2}</Typography>
              </Box>
            )}
            <Box>
              <Typography variant={'body2'} sx={{ marginTop: '2px' }}>
                {patient.address.city},&nbsp;{patient.address.state}&nbsp;{patient.address.zip}
              </Typography>
            </Box>
            <Box style={{ display: 'flex', flexDirection: 'row' }}>
              <Box>
                {patient.inHomeVisitServiceRange && (
                  <Tooltip title='In home visit service area'>
                    <IconButton size={'small'} sx={{ padding: 0 }}>
                      <Check className='icon-green' sx={{ fontSize: 12 }} />
                      <Typography
                        className='icon-green'
                        variant={'body2'}
                        sx={{ marginLeft: '4px', fontSize: 12, fontWeight: 600 }}
                      >
                        IPV
                      </Typography>
                    </IconButton>
                  </Tooltip>
                )}
                {!patient.inHomeVisitServiceRange && (
                  <Tooltip title='Not in home visit service area'>
                    <IconButton sx={{ padding: 0 }}>
                      <Close className='icon-error' sx={{ fontSize: 12 }} />
                      <Typography
                        className='icon-error'
                        variant={'body2'}
                        sx={{ marginLeft: '4px', fontSize: 12, fontWeight: 600 }}
                      >
                        IPV
                      </Typography>
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Box sx={{ marginLeft: '8px' }}>
                {capsuleEligibilityCheckInProgress && (
                  <Box className='spinner-box'>
                    <Tooltip title='Checking for Capsule delivery availability'>
                      <CircularProgress
                        className='icon-error'
                        sx={{ marginTop: '8px', marginRight: '8px', marginBottom: '8px' }}
                      />
                    </Tooltip>
                  </Box>
                )}
                {!capsuleEligibilityCheckInProgress && !capsuleDeliveryAvailable && (
                  <Tooltip title='Capsule delivery is not avialable'>
                    <IconButton sx={{ padding: 0 }}>
                      <Close className='icon-error' sx={{ fontSize: 12 }} />
                      <Typography
                        className='icon-error'
                        variant={'body2'}
                        sx={{ marginLeft: '4px', fontSize: 12, fontWeight: 600 }}
                      >
                        Capsule
                      </Typography>
                    </IconButton>
                  </Tooltip>
                )}
                {!capsuleEligibilityCheckInProgress && capsuleDeliveryAvailable && (
                  <Tooltip title='Capsule delivery is avialable'>
                    <IconButton sx={{ padding: 0 }}>
                      <Check className='icon-green' sx={{ fontSize: 12 }} />
                      <Typography
                        className='icon-green'
                        variant={'body2'}
                        sx={{ marginLeft: '4px', fontSize: 12, fontWeight: 600 }}
                      >
                        Capsule
                      </Typography>
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
}

export default PatientInfoContact;
